import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CommunicationService,
  EmailTemplate,
} from '../../../services/communication-service/communication.service';
// import { LocalHistoryService } from '../../../services/local-history-service/local-history.service';
import { ModalService } from '../../../services/modal/modal.service';
import { RescueService } from '../../../services/rescue-service/rescue.service';

@Component({
  selector: 'app-edit-rescue',
  standalone: false,
  templateUrl: './edit-rescue.html',
  styleUrl: './edit-rescue.scss',
})
export class EditRescue {
  rescues: any;
  private actionKey: string | null = null;
  private actionSignature: string | null = null;
  loading: boolean = true;
  rescueStatuses: any;
  selectedStatus: any;

  templates: EmailTemplate[] = [];
  private metadataContext: (() => boolean) | null = null;
  metadataReady = false;
  metadataError = '';

  readonly dialogRef = inject(MatDialogRef<EditRescue>);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { rescues: any; selectionIsCurrent: () => boolean },
    @Inject(RescueService) private rescueService: RescueService,
    @Inject(CommunicationService) private communicationService: CommunicationService,
    // @Inject(LocalHistoryService) private localHistoryService: LocalHistoryService,
    @Inject(ModalService) private modalService: ModalService,
  ) {
    this.rescues = data.rescues;
  }

  async ngAfterViewInit() {
    await this.carregarOpcoes();
  }

  private contextIsCurrent(): boolean {
    return !!this.data.selectionIsCurrent?.() && !!this.metadataContext?.();
  }

  private invalidateContext() {
    this.metadataReady = false;
    this.metadataContext = null;
    this.templates = [];
    this.rescueStatuses = [];
    this.rescues = [];
    this.selectedStatus = null;
    this.metadataError =
      'A sessão ou a API mudou. Feche esta janela e pesquise os pedidos novamente.';
    this.modalService.error(this.metadataError, 'Recarregue os pedidos');
  }

  async carregarOpcoes() {
    this.loading = true;
    this.metadataReady = false;
    this.metadataError = '';
    const context = this.communicationService.captureContext();
    try {
      if (!this.data.selectionIsCurrent?.()) {
        this.invalidateContext();
        return;
      }
      const [statuses, templates] = await Promise.all([
        this.rescueService.getRescueStatus(),
        this.communicationService.getTemplates(),
      ]);
      if (!this.data.selectionIsCurrent() || !context()) {
        this.invalidateContext();
        return;
      }
      if (
        !Array.isArray(statuses) ||
        !statuses.every(
          (status) =>
            status &&
            Number.isSafeInteger(Number(status.id_status)) &&
            Number(status.id_status) > 0 &&
            typeof status.descricao === 'string',
        ) ||
        !Array.isArray(templates)
      ) {
        throw Error('A consulta não retornou listas válidas de status e modelos. Tente novamente.');
      }
      this.rescueStatuses = statuses;
      this.templates = templates;
      this.metadataContext = context;
      this.metadataReady = true;
    } catch (error: any) {
      this.metadataError =
        error?.error?.message || error?.message || 'Não foi possível carregar status e templates.';
      this.modalService.error(this.metadataError, 'Erro ao carregar');
    } finally {
      this.loading = false;
    }
  }

  get quantidade(): number {
    return Array.from(this.rescues ?? []).length;
  }

  /**
   * O e-mail não é escolhido pelo administrador: ele decorre do status.
   * Cada status tem no máximo um template ativo associado.
   */
  get templateDoStatus(): EmailTemplate | null {
    if (!this.selectedStatus) {
      return null;
    }

    return (
      this.templates.find(
        (template) =>
          Number(template.status_associado) === Number(this.selectedStatus) && template.ativo,
      ) ?? null
    );
  }

  get nomeTemplate(): string {
    return this.templateDoStatus?.nome ?? '';
  }

  descricaoStatus(): string {
    const encontrado = (this.rescueStatuses || []).find(
      (status: any) => Number(status.id_status) === Number(this.selectedStatus),
    );
    return encontrado ? encontrado.descricao : '';
  }

  /**
   * A ausência de template não bloqueia: status sem e-mail configurado é
   * alterado do mesmo jeito, só sem comunicação.
   */
  podeSalvar(): boolean {
    return (
      this.contextIsCurrent() &&
      this.metadataReady &&
      !this.loading &&
      !!this.selectedStatus &&
      this.rescueStatuses.some(
        (status: { id_status: number | string }) =>
          Number(status.id_status) === Number(this.selectedStatus),
      ) &&
      this.quantidade > 0 &&
      this.quantidade <= 100
    );
  }

  /**
   * Primeiro aviso: a alteração de status em si. O segundo aviso, o do
   * disparo em massa, só abre depois que este fecha — o botão do modal executa
   * a `action` antes de fechar, então encadear pelo `afterClosed()` evita
   * empilhar um dialog sobre outro que está saindo.
   */
  confirmar() {
    if (!this.contextIsCurrent()) {
      this.invalidateContext();
      return;
    }
    if (!this.podeSalvar()) {
      return;
    }

    let continuar = false;

    const dialogRef = this.modalService.custom({
      type: 'warning',
      title: 'Confirmar alteração de status',
      message:
        `${this.quantidade} pedido(s) passarão para o status ` + `"${this.descricaoStatus()}".`,
      showCloseButton: true,
      listButtons: [
        { textButton: 'Cancelar', fill: 'outline' },
        { textButton: 'Continuar', fill: 'solid', action: () => (continuar = true) },
      ],
    });

    dialogRef.afterClosed().subscribe(() => {
      if (!this.contextIsCurrent()) {
        this.invalidateContext();
        return;
      }
      if (continuar) {
        this.avisarComunicacao();
      }
    });
  }

  /**
   * Segundo aviso: o efeito da alteração sobre a comunicação. É ciência, não
   * escolha — se o status tem e-mail associado, ele sai.
   */
  private avisarComunicacao() {
    if (!this.contextIsCurrent()) {
      this.invalidateContext();
      return;
    }
    const temTemplate = !!this.templateDoStatus;

    const mensagem = temTemplate
      ? `O e-mail "${this.nomeTemplate}" será enviado somente para os pedidos que mudarem de status. Pedidos que já estão em "${this.descricaoStatus()}" não geram novo envio.`
      : `Não há e-mail configurado para o status "${this.descricaoStatus()}". ` +
        'Somente o status será alterado.';

    this.modalService.custom({
      type: 'warning',
      title: temTemplate ? 'Esta ação vai comunicar em massa' : 'Nenhum e-mail será enviado',
      message: mensagem + '\n\nA ação fica registrada no histórico.',
      showCloseButton: true,
      listButtons: [
        {
          textButton: temTemplate ? 'Não comunicar' : 'Cancelar',
          fill: 'outline',
          action: () => (temTemplate ? this.save(false) : null),
        },
        {
          textButton: temTemplate ? 'Confirmar e comunicar' : 'Confirmar',
          fill: 'solid',
          action: () => (temTemplate ? this.save(true) : this.save(false)),
        },
      ],
    });
  }

  async save(sendCommunication: boolean) {
    if (!this.contextIsCurrent()) {
      this.invalidateContext();
      return;
    }
    if (this.loading || !this.metadataReady) return;
    this.loading = true;
    const ids = Array.from(this.rescues)
      .map((item: any) => Number(item.id_resgate))
      .sort((a, b) => a - b);
    const signature = JSON.stringify({
      ids,
      status: Number(this.selectedStatus),
      sendCommunication,
    });
    if (!this.actionKey || signature !== this.actionSignature) {
      this.actionKey = crypto.randomUUID();
      this.actionSignature = signature;
    }
    try {
      const result = await this.communicationService.processOrders({
        status: Number(this.selectedStatus),
        resgates: ids,
        template_id: this.templateDoStatus?.id ?? null,
        comunicado_por_email: sendCommunication,
        chave_idempotencia: this.actionKey,
      });
      const summary =
        `${result.atualizados} pedido(s) atualizado(s) para "${this.descricaoStatus()}". ` +
        `${result.enviados} envio(s) aceito(s) pelo provedor. ` +
        (result.sem_alteracao
          ? `${result.sem_alteracao} pedido(s) já estavam nesse status; nenhum e-mail repetido. `
          : '');
      if (result.atualizados === 0 && result.sem_alteracao > 0)
        this.modalService.warning(
          `Nenhum pedido foi alterado e nenhum e-mail foi enviado: os pedidos selecionados já estavam em "${this.descricaoStatus()}". Para reenviar uma comunicação sem mudar o status, use Disparos Pontuais.`,
          'Nenhuma alteração realizada',
        );
      else if (result.falhas.length)
        this.modalService.warning(
          summary +
            `${result.falhas.length} envio(s) sem confirmação. Consulte o Histórico de Comunicações.`,
          'Processamento concluído com pendências',
        );
      else
        this.modalService.success(
          summary +
            (!sendCommunication || !result.template
              ? 'Nenhum e-mail previsto para esta ação.'
              : ''),
          'Processamento concluído',
        );
      this.dialogRef.close(this.rescues);
    } catch (error: any) {
      this.modalService.error(
        error?.error?.message ||
          error?.message ||
          'Não foi possível confirmar a operação. Consulte o histórico antes de tentar novamente.',
        'Erro na operação',
      );
    } finally {
      this.loading = false;
    }
  }
}
